import * as React from 'react'
import { renderRichtext } from '../../lib/utils/renderRichtext'
import { useForm } from '../../lib/hooks/useForm'
import { useContactFormReasonOptions } from '../../lib/hooks/useContactFormReasonOptions'
import { TextInput } from '../../components/UI/TextInput'
import { Textarea } from '../../components/UI/Textarea'
import { Select } from '../../components/UI/Select'
import { Button } from '../../components/UI/Button'
import { Headline } from '../../components/UI/Headline'
import { Recaptcha } from '../../components/Recaptcha'

import useEventTracking from '../../lib/hooks/tracking'
import getDomainFromEmail from '../../lib/utils/getDomainFromEmail'
import { sbEditable } from '@storyblok/storyblok-editable'
import { useEffect, useState, useCallback } from 'react'

import emailValidator from 'email-validator';
import posthog from 'posthog-js'
import 'react-phone-input-2/lib/style.css'
import { Heading } from '../../types'

import { containerExpanded } from '../../lib/styles/Container'
import styled from 'styled-components'
import PhoneInput from 'react-phone-input-2'
import { StyledButton } from '../../components/UI/Button/styled'
import { StyledHelpList } from '../../components/UI'
import { ContactPanelProps, IContactFormValues } from '../ContactPanel/types'
import { debounce } from 'lodash'

export const StyledWrapper = styled.div<{
  marginBlock?: string
  $width?: string
}>`
  ${containerExpanded};
  margin: 3rem auto;
  width: 100%;
  max-width: ${props => props.$width || '100%'};
  padding: 0 1rem;

  @media (max-width: 992px) {
    max-width: 100%;
    padding: 0 1.5rem;
  }

  @media (max-width: 640px) {
    max-width: 100%;
    padding: 0;
    margin: 2rem auto;
  }
`

export const TextInputHidden = styled.div`
  display: none;
`

export const StyledPhoneInput = styled(PhoneInput)`
  &.react-tel-input {
    .form-control {
      border: 0;
      border-radius: 0.6rem;
      font-size: 0.9rem;
      height: 2.9rem;
      line-height: 1;
      outline: 0;
      transition: all 0.2s;
      width: 100%;
      background: rgba(255,255,255,.6);
      color: #0D0D0D;
      padding-left: 3rem;
      
      &:focus {
        background: rgba(255,255,255,.8);
      }
    }
    
    .flag-dropdown {
      background: transparent;
      border: 0;
      
      &.open {
        background: rgba(255,255,255,.8);
        border-radius: 0.6rem 0 0 0;

        .selected-flag {
          border-radius: 0.6rem 0 0 0;
        }
      }
      .selected-flag {
        border-radius: 0.6rem 0 0 0.6rem;
        background: transparent;
      }
    }
  }
`

export const StyledPanel = styled.div`
  align-items: center;
  background-color: #D8EDFD;
  border-radius: 1.5rem;
  padding: 3rem 2rem;
  width: 100%;

  @media screen and (max-width: 992px) {
    padding: 2rem 1.5rem;
    border-radius: 1rem;
  }

  @media screen and (max-width: 480px) {
    padding: 1.5rem 1rem;
    border-radius: 0.75rem;
  }
`

export const StyledBody = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 4rem;

  @media screen and (max-width: 860px) {
    flex-direction: column;
    gap: 2rem;
  }
`

export const StyledHeader = styled.div`
  display: flex;
  margin-bottom: 2rem;

  @media screen and (max-width: 860px) {
    margin-bottom: 1.5rem;
  }

  @media screen and (min-width: 860px) {
    & > * {
      flex: 0.66;
      padding-right: 4rem;
    }
  }
`

export const StyledForm = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.5rem;

  ${StyledButton} {
    align-self: stretch;
    margin-top: 1.5rem;
    
    @media screen and (min-width: 480px) {
      align-self: flex-start;
    }
  }

  @media screen and (max-width: 480px) {
    gap: 0.75rem;
  }
`

export const StyledInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  // @media screen and (min-width: 860px) {
  //   flex-direction: row;
  // }

  & > * {
    flex: 1;
  }
`

export const StyledContactInformations = styled.div`
  align-items: flex-start;
  display: flex;
  flex: 0.34;
  flex-direction: column;
  gap: 0.5rem;

  &.prose > p,
  &.prose > ${StyledHelpList} {
    margin-bottom: 0;
    margin-top: 0;
  }
`

export const StyledSuccessMessage = styled.p`
  white-space: pre-line;
`

export const StyledErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.red[3]};
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 0.6rem;
`


export const forbiddenDomains = [
  'gmail',
  'aol',
  'yahoo',
  'hotmail',
  'outlook',
  '163',
  'icloud',
  'protonmail',
  'seznam',
  'orange',
  'qq',
  'drmail',
  'ymail',
  'teml',
  'gmx',
  't-online',
  'live',
  'libero',
  'comcast',
  'naver',
  'yopmail',
  'googlemail',
  'wanadoo',
  'tiscali',
  'yandex'
];

export const containsForbiddenDomain = (email) => {
  const domain = email.split('@')[1];
  const domainCheck = forbiddenDomains.some((forbiddenDomain) =>
    domain.includes(forbiddenDomain)
  );
  domainCheck && posthog.capture('validation_triggered', { domain: domain });
  return domainCheck;
};

export const ContactPanelV2: React.FC<{ blok: ContactPanelProps }> = ({ blok }) => {
  const [formState, setFormState] = useState({
    email: '',
    phone: '',
    selection: '',
  });

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const emailRef = React.useRef<HTMLInputElement>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  const contactFormReasonOptions = useContactFormReasonOptions();
  const trackEvent = useEventTracking();

  const { handleSubmit, status, statusCode } = useForm<IContactFormValues>({
    form: formRef.current,
    onSuccess: formData => {
      trackEvent('contact_form_capterra_submitted', {
        contact_company: formData.company,
        contact_email: getDomainFromEmail(formData.email),
        contact_first_name: formData.firstname,
        contact_last_name: formData.lastname,
        contact_message: formData.message,
        contact_reason: formData.reason,
        contact_phone: formData.phone,
      });
    },
  });

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const emailInput = emailRef.current?.value;
    if (emailInput) {
      if (!emailValidator.validate(emailInput)) {
        setErrors(prev => ({
          ...prev,
          email: 'Please enter a valid email address.'
        }));
        return;
      }
      if (containsForbiddenDomain(emailInput)) {
        const domain = emailInput.split('@')[1];
        setErrors(prev => ({
          ...prev,
          email: `Please enter your business email address. This form does not accept addresses from ${domain}.`
        }));
        return;
      }
    }

    if (errors.phone) return;
    setErrors(prev => ({ ...prev, email: '' }));
    handleSubmit(event);
  };

  const updateFormState = (field: string, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  return (
    <StyledWrapper marginBlock={blok.marginBlock} {...sbEditable(blok)}>
      <StyledPanel>
        {blok?.headline && (
          <StyledHeader>
            <Headline
              is={blok.headlineElement}
              as={blok.headlineSize?.replace('is-', '') as Heading}
            >
              {blok.headline}
            </Headline>
          </StyledHeader>
        )}
        <StyledBody>
          {status === 'success' ? (
            <StyledSuccessMessage>{blok.confirmationText}</StyledSuccessMessage>
          ) : (
            <StyledForm
              action={`${process.env.GATSBY_PDFTOOLS_API_URL}/ContactForm`}
              onSubmit={handleFormSubmit}
              method="POST"
              ref={formRef}
            >
              <StyledInputGroup>
                <TextInput
                  name="firstname"
                  variant="light"
                  label={blok.firstNameLabel}
                  required
                />
                <TextInput
                  name="lastname"
                  variant="light"
                  label={blok.lastNameLabel}
                  required
                />
                <TextInput
                  ref={emailRef}
                  name="email"
                  type="email"
                  variant="light"
                  label={blok.emailLabel}
                  required
                />
                <TextInput
                  name="phone"
                  variant="light"
                  label={blok.phoneNumberLabel}
                  value={formState.phone}
                  onChange={e => updateFormState('phone', e.currentTarget.value)}
                />
                <Select
                  name="reason"
                  value={formState.selection}
                  onChange={e => updateFormState('selection', e.currentTarget.value)}
                  variant="light"
                  label={blok.productLabel}
                  required
                >
                  {contactFormReasonOptions.map((b, index) => (
                    <option key={`key-${index}`} value={b.value}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </StyledInputGroup>

              <Textarea name="message" variant="light" label={blok.messageLabel} required></Textarea>
              <Recaptcha />

              {(status === 'error' || errors.email || errors.phone) && (
                <StyledErrorMessage>
                  {statusCode === 400 && blok.validationError}
                  {statusCode === 503 && blok.processingFailure}
                  {errors.email}
                  {errors.phone}
                </StyledErrorMessage>
              )}

              <Button
                type="submit"
                disabled={status === 'loading' || !!errors.email}
                $ignoreTheme
              >
                {blok.sendButtonText}
              </Button>
            </StyledForm>
          )}
        </StyledBody>
      </StyledPanel>
    </StyledWrapper>
  );
}
